import React from 'react';
import pick from 'lodash/pick';
import {useNavigate} from 'react-router';

import {useTranslation} from 'common/i18n/hooks';
import {Content} from 'common/types/app-api/content';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {useCanvasApi} from 'apps/metric-tree/src/api/canvas';
import {useExperimentsApi} from 'apps/experiments/src/api/experiments';
import {useFeatureFlagsApi} from 'apps/feature-flags/src/api/feature-flags';
import {UPDATEABLE_EXPERIMENT_FIELDS} from 'apps/experiments/src/components/utils';
import {useExperimentsStore} from 'apps/experiments/src/store';

import {TrackingEvent, TrackingSource} from '../tracking';
import {getContentPageUrl, getContentTypeName, performContentAction} from '../helpers';

export type UseDuplicateContentParams = {
  projectId: number | string;
  workspaceId?: number | string;
  redirectAfterSuccess?: boolean;
  source?: TrackingSource;
};

export function useDuplicateContent({projectId, workspaceId, redirectAfterSuccess, source}: UseDuplicateContentParams) {
  const i18n = useTranslation();
  const navigate = useNavigate();
  projectId = parseInt(projectId as string, 10);
  workspaceId = workspaceId ? parseInt(workspaceId as string, 10) : 0;
  const setAllowNavigatingAway = useExperimentsStore((state) => state.setAllowNavigatingAway);
  const {duplicateCanvas} = useCanvasApi({projectId, workspaceId});
  const {duplicateExperiment} = useExperimentsApi({projectId, workspaceId});
  const {duplicateFeatureFlag} = useFeatureFlagsApi({projectId, workspaceId});

  return React.useCallback(
    (content: Content, onSuccess?: (result: Content) => void) => {
      const entityTypeName = getContentTypeName(content, i18n);
      return performContentAction({
        content,
        action: () => {
          switch (content.content_type) {
            case SharableEntityType.MetricTree:
              return duplicateCanvas({id: content.id});
            case SharableEntityType.Experiment:
              setAllowNavigatingAway(true);
              return duplicateExperiment({
                id: content.id,
                ...pick(content, UPDATEABLE_EXPERIMENT_FIELDS),
              }) as Promise<Content>;
            case SharableEntityType.FeatureFlag:
              return duplicateFeatureFlag({id: content.id}) as Promise<Content>;
            default:
              throw new Error(`Unsupported content type: ${content.content_type}`);
          }
        },
        onSuccess: (result: Content) => {
          onSuccess?.(result);
          if (redirectAfterSuccess) {
            navigate(getContentPageUrl(result, {projectId, workspaceId}));
          }
        },
        onError: () => {
          if (content.content_type === SharableEntityType.Experiment) {
            setAllowNavigatingAway(false);
          }
        },
        successMessage: i18n.t(`sidenav.duplicate-content`, {entityTypeName, name: content.name}),
        errorMessage: i18n.t(`sidenav.duplicate-content-error`, {entityTypeName, name: content.name}),
        successTrackingEvent: TrackingEvent.DuplicateContent,
        successTrackingProps: {
          success: true,
        },
        errorTrackingEvent: TrackingEvent.DuplicateContent,
        errorTrackingProps: {
          success: false,
        },
        source,
      });
    },
    [i18n, navigate, projectId, workspaceId, redirectAfterSuccess, source],
  );
}
